@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import com.willandsons.lovenearby.components.layouts.MarkdownLayout as _com_willandsons_lovenearby_components_layouts_MarkdownLayout
import com.willandsons.lovenearby.components.layouts.PageLayout as _com_willandsons_lovenearby_components_layouts_PageLayout
import com.willandsons.lovenearby.pages.AboutPage as _com_willandsons_lovenearby_pages_AboutPage
import com.willandsons.lovenearby.pages.HomePage as _com_willandsons_lovenearby_pages_HomePage
import com.willandsons.lovenearby.pages.PrivacyPage as _com_willandsons_lovenearby_pages_PrivacyPage
import com.willandsons.lovenearby.pages.TermsPage as _com_willandsons_lovenearby_pages_TermsPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "LoveNearby"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "com.willandsons.lovenearby.components.layouts.MarkdownLayout",
            parentLayoutId = "com.willandsons.lovenearby.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.willandsons.lovenearby.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _com_willandsons_lovenearby_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "com.willandsons.lovenearby.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _com_willandsons_lovenearby_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "com.willandsons.lovenearby.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.willandsons.lovenearby.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_lovenearby_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "com.willandsons.lovenearby.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.willandsons.lovenearby.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_lovenearby_pages_AboutPage()
        }
        ctx.router.register(
            "/privacy",
            layoutId = "com.willandsons.lovenearby.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.willandsons.lovenearby.pages.initPrivacyPage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_lovenearby_pages_PrivacyPage()
        }
        ctx.router.register(
            "/terms",
            layoutId = "com.willandsons.lovenearby.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.willandsons.lovenearby.pages.initTermsPage(ctx)
            },
        ) { pageCtx -> 
            _com_willandsons_lovenearby_pages_TermsPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", com.willandsons.lovenearby.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", com.willandsons.lovenearby.SubheadlineTextStyle)
        ctx.theme.registerStyle("footer", com.willandsons.lovenearby.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", com.willandsons.lovenearby.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("markdown", com.willandsons.lovenearby.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", com.willandsons.lovenearby.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("hero-container", com.willandsons.lovenearby.pages.HeroContainerStyle)
        ctx.theme.registerStyle("home-grid", com.willandsons.lovenearby.pages.HomeGridStyle)
        ctx.theme.registerStyle("home-grid-cell", com.willandsons.lovenearby.pages.HomeGridCellStyle)
        ctx.theme.registerVariant("-circle", com.willandsons.lovenearby.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", com.willandsons.lovenearby.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", com.willandsons.lovenearby.components.sections.SideMenuSlideInAnim)
        com.willandsons.lovenearby.initSiteStyles(ctx)
        com.willandsons.lovenearby.initColorMode(ctx)
        com.willandsons.lovenearby.initStyles(ctx)
        com.willandsons.lovenearby.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        com.willandsons.lovenearby.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
